import React from 'react';

import { discounted_upgrade_path, upgrade_subscription_path } from '@routes';

import { EventAvailability } from './constants';
import { Event, EventRegistrationProps } from './interfaces';

const EventRegistration = ({ event, eventOffer, upgradeOffer }: EventRegistrationProps) => {
  if (event.registrationHasPassed) {
    return (
      <div className="stay-tuned">
        <div className="stay-header past-event">This event has passed.</div>
      </div>
    );
  }

  switch (event.availability) {
    case EventAvailability.NotYetOpen:
      return (
        <div className="stay-tuned">
          <div className="stay-header">Stay Tuned for More Information</div>
          <div className="stay-details">
            Tickets for this event aren&apos;t available yet. Check back soon for updates.
          </div>
        </div>
      );

    case EventAvailability.InviteOnly:
      return (
        <div className="stay-tuned">
          <div className="stay-header">This event is invitation only.</div>
        </div>
      );

    case EventAvailability.SoldOut:
      return <SoldOutRegistration event={event} />;

    case EventAvailability.SoldOutToSubsOpenToLeads:
      if (event.isCurrentUserSubscriber) {
        return <SoldOutRegistration event={event} />;
      } else {
        return <SubscribeForEvent eventOffer={eventOffer} />;
      }

    case EventAvailability.OpenToAll:
      return <RegisterForEvent event={event} />;

    default:
      break;
  }

  if (!event.isCurrentUserSubscriber) {
    return <SubscribeForEvent eventOffer={eventOffer} />;
  }

  // User is a subscriber
  if (event.canRegisterNow) {
    return <RegisterForEvent event={event} />;
  } else if (event.isCurrentUserYPSubscriber) {
    return <PurchaseTicketForYP event={event} />;
  } else {
    return <UpgradeForEvent event={event} upgradeOffer={upgradeOffer} />;
  }
};

const SoldOutRegistration = ({ event }: { event: Event }) => (
  <a
    href={event.eventbriteUrl}
    className="button btn btn-rsvp track-click"
    data-type="button"
    data-name="SOLD OUT - Join Waitlist button"
    data-location="Event Show Page"
    target="_blank"
    rel="noreferrer"
  >
    SOLD OUT - Join Waitlist
  </a>
);

const SubscribeForEvent = ({ eventOffer }: { eventOffer: string }) => (
  <>
    <a
      href={`/subscribe?offer=${eventOffer}`}
      className="button btn btn-rsvp track-click"
      data-type="button"
      data-name="Subscribe to RSVP"
      data-location="Event Show Page"
    >
      Subscribe to R.S.V.P.
    </a>
    <p className="event-login">
      Already a subscriber?{' '}
      <a
        href="/sessions/new"
        className="track-click"
        data-name="Login"
        data-type="link"
        data-location="event registration"
      >
        Sign in here
      </a>
    </p>
  </>
);

const RegisterForEvent = ({ event }: { event: Event }) => {
  if (event.userHasRsvpd) {
    return (
      <div className="stay-tuned rsvp-success">
        <div className="stay-header">You have successfully registered for this event.</div>
        {event.conferenceCall && (
          <div className="stay-details">
            Details will be emailed to you 1 hour prior to the call.
          </div>
        )}
      </div>
    );
  }

  if (event.conferenceCall) {
    return (
      <a
        href={`/events/${event.slug}/rsvp`}
        className="button btn btn-rsvp track-click"
        data-type="button"
        data-name="Conference Call RSVP"
        data-location="Event Show Page"
      >
        {event.ctaLabel || 'R.S.V.P.'}
      </a>
    );
  }

  // Handle eventbrite URL selection logic
  let rsvpUrl = '';
  if (event.isCurrentUserSubscriber) {
    if (event.isCurrentUserProSubscriber) {
      rsvpUrl = event.proEventbriteUrl || '';
    } else if (event.isCurrentUserYPSubscriber) {
      rsvpUrl = event.ypEventbriteUrl || '';
    } else if (event.isCurrentUserAnnualSubscriber) {
      rsvpUrl = event.annualEventbriteUrl || '';
    }

    if (!rsvpUrl) {
      rsvpUrl = event.eventbriteUrl || '';
    }
  } else {
    rsvpUrl = event.leadEventbriteUrl || '';
  }

  if (!rsvpUrl) {
    rsvpUrl = `/events/${event.slug}/rsvp`;
  }

  return (
    <a
      href={rsvpUrl}
      className="button btn btn-rsvp track-click"
      data-type="button"
      data-name="Live Event RSVP"
      data-location="Event Show Page"
      target="_blank"
      rel="noopener"
    >
      {event.ctaLabel || 'R.S.V.P.'}
    </a>
  );
};

const PurchaseTicketForYP = ({ event }: { event: Event }) => {
  if (!event.eventbriteUrl) return null;

  return (
    <>
      <a
        href={event.eventbriteUrl}
        className="button btn btn-rsvp track-click"
        data-type="button"
        data-name="Live Event RSVP"
        data-location="Event Show Page"
        target="_blank"
        rel="noopener"
      >
        {event.ctaLabel || 'Purchase a Ticket'}
      </a>
      <p className="event-yp-message">
        <small>
          Thank you for being a Young Professional subscriber at The Information. Please purchase a
          ticket to attend this event.
        </small>
      </p>
    </>
  );
};

const UpgradeForEvent = ({ event, upgradeOffer }: { event: Event; upgradeOffer: string }) => {
  const label = 'Upgrade to R.S.V.P.';
  discounted_upgrade_path();
  const path = upgradeOffer
    ? discounted_upgrade_path({
        offer: upgradeOffer,
        return_to: `/events/${event.slug}`,
      })
    : upgrade_subscription_path({
        return_to: `/events/${event.slug}`,
      });

  return (
    <a
      href={path}
      className="button btn btn-rsvp track-click"
      data-type="button"
      data-name="Upgrade to RSVP"
      data-location="Event Show Page"
    >
      {label}
    </a>
  );
};

export default EventRegistration;
